import { motion } from "framer-motion";
import { ArrowRight, Wifi, Network, Server } from "lucide-react";

const services = [
  {
    id: "hfc-gpon",
    icon: Network,
    image: "https://msvision.com.ar/wp-content/uploads/2023/11/fusionadora.jpg",
    title: "Relevamiento de Campo, HFC y GPON",
    description:
      "Relevamiento técnico de infraestructura de red en campo para redes HFC y GPON. Diagnóstico, documentación y planificación de despliegue.",
  },
  {
    id: "triple-play",
    icon: Wifi,
    image: "https://msvision.com.ar/wp-content/uploads/2023/11/imagen_2023-11-08_122648725.png",
    title: "Instalación Triple Play Residencial y Corporativo",
    description:
      "Instalación completa de servicios de internet, telefonía y televisión para hogares y empresas. Configuración de ONTs, decodificadores y WiFi.",
  },
  {
    id: "cableado",
    icon: Server,
    image: "https://msvision.com.ar/wp-content/uploads/2023/11/cableado-estructurado-YMANT.jpg",
    title: "Red de Cableado Estructurado",
    description:
      "Diseño e instalación de cableado estructurado en edificios bajo sistema colectivo certificado. Cumplimiento de normas TIA/EIA e ISO/IEC.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

export const Services = () => {
  return (
    <section id="servicios" data-testid="services-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-accent font-heading font-semibold text-sm tracking-widest uppercase block mb-3">
            Lo que hacemos
          </span>
          <h2 className="font-heading font-black text-primary text-4xl md:text-5xl mb-4">
            Nuestros Servicios
          </h2>
          <p className="font-body text-gray-500 text-lg max-w-xl mx-auto">
            Soluciones integrales en telecomunicaciones adaptadas a cada proyecto
          </p>
          <div className="w-20 h-1 bg-accent mx-auto mt-6 rounded-full" />
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <motion.div
                key={svc.id}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                data-testid={`service-card-${svc.id}`}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.08)] hover:-translate-y-2 hover:shadow-[0_20px_40px_-4px_rgba(0,174,239,0.2)] hover:border-accent transition-all duration-300 cursor-pointer"
              >
                {/* Image */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div
                    className="hidden w-full h-full bg-gradient-to-br from-primary to-accent items-center justify-center"
                  >
                    <Icon size={48} className="text-white/60" />
                  </div>
                  {/* Icon badge */}
                  <div className="absolute bottom-4 right-4 w-12 h-12 bg-accent rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Icon size={22} className="text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-7">
                  <h3 className="font-heading font-bold text-primary text-xl mb-3 leading-snug">
                    {svc.title}
                  </h3>
                  <p className="font-body text-gray-500 text-sm leading-relaxed mb-5">
                    {svc.description}
                  </p>
                  <button
                    data-testid={`service-more-${svc.id}`}
                    className="flex items-center gap-2 text-accent font-heading font-semibold text-sm border border-accent px-4 py-2 rounded-full hover:bg-accent hover:text-white transition-all duration-300 group-hover:gap-3"
                  >
                    Ver más <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
